import { useEffect, useRef } from "react"
import { ChevronDown, ChevronUp } from "react-feather"
import { useTranslation } from "react-i18next"
import type { LinkProps } from "next/link"
import { useRouter } from "next/router"
import clsx from "clsx"

import LazyImage from "~/components/LazyImage"
import { Link } from "~/components/Link"
import { getClassesByColorScheme } from "~/utils/style/colorSchemes"

import { AUTHOR_QUERY_PARAM } from "./constants"

export type AuthorCardProps = {
  uid: string
  name: string
  profilePic: string
}

export const AuthorCard = ({
  uid,
  name,
  profilePic,
}: AuthorCardProps): JSX.Element | null => {
  const { t } = useTranslation("contentThemePage")
  const router = useRouter()
  const ref = useRef<HTMLDivElement>(null)
  const bannerClasses = getClassesByColorScheme({})
  const pathname = router.asPath.split("?")[0]

  const getSelectededLink = (authorUid: string): LinkProps["href"] => ({
    pathname,
    query:
      router.query[AUTHOR_QUERY_PARAM] === authorUid
        ? undefined
        : { [AUTHOR_QUERY_PARAM]: authorUid },
  })

  const isSelected = router.query[AUTHOR_QUERY_PARAM] === uid

  useEffect(() => {
    if (!isSelected) return
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [isSelected])

  return (
    <div ref={ref} className="relative flex flex-col justify-end pb-[100%]">
      <LazyImage
        className="absolute top-0 left-0 h-full w-full object-cover"
        src={profilePic}
        imgixParams={{ crop: "faces,center", fit: "crop", auto: "format" }}
        alt=""
      />
      <header className="absolute bottom-0 left-0 z-0 flex flex-col items-start">
        <h3
          className={clsx(
            "inline-flex",
            "mt-16 py-1 px-2",
            "text-lg",
            isSelected
              ? "bg-primary-50 dark:bg-primary-700"
              : bannerClasses.bgContrast,
            bannerClasses.text,
          )}
        >
          <Link
            className="flex items-end justify-between"
            href={getSelectededLink(uid)}
            shallow
          >
            {name}

            <span className="ml-2 self-end">
              <span className="sr-only">
                {isSelected ? t("authorCard.seeLess") : t("authorCard.seeMore")}
              </span>
              {isSelected ? <ChevronUp /> : <ChevronDown />}
            </span>
          </Link>
        </h3>
      </header>
    </div>
  )
}
